import {
  Box,
  buildPath,
  buildTemplatePath,
  Button,
  DataArray,
  DataDisplay,
  deepCopy,
  formatDate,
  Grid,
  GridBreak,
  newToast,
  Page,
  PageRoute,
  pn,
  Stack,
  Tab,
  TabContext,
  TabList,
  Typography,
  useElementBreakpoints,
  useParams,
  useValidationErrors,
} from "@compass/core";
import {
  FormGroup as MuiFormGroup,
  RadioGroupProps as MuiRadioGroupProps,
  FormControl,
  FormLabel,
  Checkbox,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  CircularProgress
} from "@mui/material";
import HelpIcon from "@mui/icons-material/Help";
import { FormControlLabel } from "@mui/material";
import { DatePicker, Input, Logo } from "components";
import EscalatorWarningIcon from "@mui/icons-material/EscalatorWarning";
import { SignUpSignature } from "pages/students/student-service-templates/StudentServiceTemplateSignUpPage/SignUpSignature";
import {
  Student,
  StudentService,
  StudentServiceTemplateSignUpRequest,
  StudentServiceTemplateSignUpResource,
} from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import { thankYouPageRoute } from "pages/ThankYouPage/ThankYouPage";
import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";
import { Html5QrcodeScanner, Html5Qrcode, Html5QrcodeResult } from "html5-qrcode";
import QrCodeScannerSharpIcon from '@mui/icons-material/QrCodeScannerSharp';
import React from 'react';

const routeName = "studentServiceSignUp";
const GRID_SPACING = 2;
const STACK_SPACING = 1;

class Params {
  tenantName: string = "";
  id: number = 0;
  validationToken: string = "";
}

interface QRScannerConfig {
  fps: number;
  qrbox: { width: number; height: number };
  aspectRatio?: number;
  showTorchButtonIfSupported?: boolean;
  showZoomSliderIfSupported?: boolean;
  defaultZoomValueIfSupported?: number;
}

interface QRScanResult {
  decodedText: string;
  result: any;
}

interface QRScannerState {
  isActive: boolean;
  error: string | null;
  showFileUpload: boolean;
}

export type SignUpTab = "Student ID" | "Student Lookup";
const tabs: SignUpTab[] = ["Student ID", "Student Lookup"];

export const studentServiceTemplateSignUpPageRoute: PageRoute<Params, undefined> = {
  handle: "Sign Up for Student Service",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
};

export const InfoIcon = ({ text }: { text: string }) => {
  return (
    <Tooltip title={text}>
      <HelpIcon
        fontSize="inherit"
        style={{
          fontSize: "1.0em",
          verticalAlign: "super",
          marginLeft: 4,
          color: "green",
          cursor: "pointer",
        }}
      />
    </Tooltip>
  );
};

const useQRScanner = () => {
  const [scannerState, setScannerState] = useState<QRScannerState>({
    isActive: false,
    error: null,
    showFileUpload: false
  });
  
  const scannerRef = useRef<Html5QrcodeScanner | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getScannerConfig = useCallback((): QRScannerConfig => {
    const isMobile = window.innerWidth < 768;
    const containerWidth = Math.min(isMobile ? 300 : 400, window.innerWidth - 50);
    const qrboxSize = Math.min(isMobile ? 200 : 250, containerWidth - 50);
    
    return {
      fps: isMobile ? 5 : 10,
      qrbox: { width: qrboxSize, height: qrboxSize },
      aspectRatio: 1.0,
      showTorchButtonIfSupported: true,
      showZoomSliderIfSupported: !isMobile,
      defaultZoomValueIfSupported: 2
    };
  }, []);

  const validateStudentId = useCallback((text: string): { isValid: boolean; error?: string } => {
    const sanitizedText = text.trim();
    
    const isValidFormat = /^[A-Za-z0-9\-_]{3,20}$/.test(sanitizedText);
    
    if (!isValidFormat) {
      return {
        isValid: false,
        error: "Invalid student ID format. Must be 3-20 characters (letters, numbers, hyphens, or underscores only)."
      };
    }
    
    return { isValid: true };
  }, []);

  const playFailureSound = useCallback(() => {
  try {
    const audio = new Audio('/sounds/QR_Fail.wav');
    audio.volume = 0.8; 
    audio.play().catch(error => {
      console.warn('Could not play QR failure sound:', error);
    });
  } catch (error) {
    console.warn('Audio not supported:', error);
  }
}, []);

  const handleScanSuccess = useCallback((
    decodedText: string, 
    decodedResult: any,
    onSuccess: (studentId: string) => void
  ) => {
    const validation = validateStudentId(decodedText);
    
    if (!validation.isValid) {
      setScannerState(prev => ({ ...prev, error: validation.error || "Invalid QR code" }));
      playFailureSound();
      return;
    }

    setScannerState({ isActive: false, error: null, showFileUpload: false });
    onSuccess(decodedText.trim());
  }, [validateStudentId, playFailureSound ]);

  const handleScanError = useCallback((errorMessage: string) => {
    const nonCriticalErrors = [
      "No QR code found",
      "QR code parse error",
      "No MultiFormat Readers were able to detect the code"
    ];
    
    const isCritical = !nonCriticalErrors.some(error => 
      errorMessage.toLowerCase().includes(error.toLowerCase())
    );
    
    if (isCritical && scannerState.isActive) {
      setScannerState(prev => ({ 
        ...prev, 
        error: "Camera access or scanning error. Please try again or upload a file." 
      }));
      console.warn("QR scan error: ", errorMessage);
    }
  }, [scannerState.isActive]);

  const initializeScanner = useCallback(async (): Promise<boolean> => {
    setScannerState(prev => ({ ...prev, error: null, showFileUpload: false }));
    
    try {
      const devices = await Html5Qrcode.getCameras();
      
      if (!devices || devices.length === 0) {
        setScannerState(prev => ({ 
          ...prev, 
          error: "No cameras found. Please upload a QR code image instead.", 
          showFileUpload: true 
        }));
        return false;
      }
      
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      
      if (errorMessage.includes("Permission denied") || errorMessage.includes("NotAllowedError")) {
        setScannerState(prev => ({ 
          ...prev, 
          error: "Camera permission denied. Please allow camera access or upload a file.", 
          showFileUpload: true 
        }));
      } else {
        setScannerState(prev => ({ 
          ...prev, 
          error: "Camera unavailable. Please upload a QR code image.", 
          showFileUpload: true 
        }));
      }
      
      console.error("Camera initialization failed:", error);
      return false;
    }
  }, []);

  const handleFileUpload = useCallback(async (
    event: React.ChangeEvent<HTMLInputElement>,
    onSuccess: (studentId: string) => void
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setScannerState(prev => ({ ...prev, error: "Please select a valid image file." }));
      playFailureSound()
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setScannerState(prev => ({ ...prev, error: "File too large. Please select an image under 10MB." }));
      playFailureSound()
      return;
    }

    try {
      const qrCodeReader = new Html5Qrcode("qr-reader-file");
      const decodedText = await qrCodeReader.scanFile(file, true);
      
      const validation = validateStudentId(decodedText);
      if (!validation.isValid) {
        setScannerState(prev => ({ ...prev, error: validation.error || "Invalid QR code" }));
         playFailureSound()
        return;
      }
      
      setScannerState({ isActive: false, error: null, showFileUpload: false });
      onSuccess(decodedText.trim());
      
      event.target.value = '';
    } catch (error) {
      setScannerState(prev => ({ 
        ...prev, 
        error: "Could not decode QR code from image. Please try a different file or ensure the QR code is clearly visible." 
      }));
      playFailureSound();
      console.error("File scan error:", error);
    }
  }, [validateStudentId, playFailureSound]);

  return {
    scannerState,
    setScannerState,
    scannerRef,
    fileInputRef,
    getScannerConfig,
    handleScanSuccess,
    handleScanError,
    initializeScanner,
    handleFileUpload
  };
};

class QRScannerErrorBoundary extends React.Component<
  { children: React.ReactNode; onError?: (error: Error) => void },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; onError?: (error: Error) => void }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('QR Scanner Error:', error, errorInfo);
    this.props.onError?.(error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box textAlign="center" p={2}>
          <Typography color="error" variant="body2">
            QR scanner encountered an error. Please refresh the page or use manual entry.
          </Typography>
        </Box>
      );
    }

    return this.props.children;
  }
}

const QRScannerSection = React.memo(({ 
  onStudentIdScanned, 
  isActive, 
  currentStudentId 
}: {
  onStudentIdScanned: (studentId: string) => void;
  isActive: boolean;
  currentStudentId: string;
}) => {
  const {
    scannerState,
    setScannerState,
    scannerRef,
    fileInputRef,
    getScannerConfig,
    handleScanSuccess,
    handleScanError,
    initializeScanner,
    handleFileUpload
  } = useQRScanner();

  const [isInitializingCamera, setIsInitializingCamera] = useState(false);

  useEffect(() => {
    let scanner = scannerRef.current;

    if (scannerState.isActive && !scanner) {
      const config = getScannerConfig();
      scanner = new Html5QrcodeScanner("qr-reader", config, false);
      scannerRef.current = scanner;
      
      scanner.render(
        (decodedText, decodedResult) => handleScanSuccess(decodedText, decodedResult, onStudentIdScanned),
        handleScanError
      );
    } else if (!scannerState.isActive && scanner) {
      scanner.clear().catch(console.error);
      scannerRef.current = null;
    }

    return () => {
      if (scannerRef.current) {
        scannerRef.current.clear().catch(console.error);
        scannerRef.current = null;
      }
    };
  }, [scannerState.isActive, getScannerConfig, handleScanSuccess, handleScanError, onStudentIdScanned, scannerRef]);

  const startScanner = async () => {
    setIsInitializingCamera(true);
    try {
      const canStart = await initializeScanner();
      if (canStart) {
        setScannerState(prev => ({ ...prev, isActive: true }));
      }
    } finally {
      setIsInitializingCamera(false);
    }
  };

  const cancelScanner = useCallback(() => {
    setScannerState({ isActive: false, error: null, showFileUpload: false });
  }, [setScannerState]);

  return (
    <Stack spacing={STACK_SPACING as any} sx={{ mt: 2 }}>
      {scannerState.isActive && (
        <Box
          id="qr-reader"
          sx={{
            width: "100%",
            maxWidth: "400px",
            margin: "0 auto",
            "& video": {
              width: "100% !important",
              height: "auto !important",
              borderRadius: 2,
            },
            "& canvas": {
              display: "none"
            }
          }}
        />
      )}

      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        flexDirection: 'column' 
      }}>
        <Stack direction="row" alignItems="center" spacing={STACK_SPACING as any}>
          <IconButton
            onClick={scannerState.isActive ? cancelScanner : startScanner}
            disabled={!isActive || isInitializingCamera}
            size="large"
            color="primary"
            aria-label={scannerState.isActive ? "Stop QR code scanner" : "Start QR code scanner"}
            sx={{
              border: '2px solid',
              borderColor: scannerState.isActive ? 'error.main' : 'primary.main',
              borderRadius: '50%',
              width: 80,
              height: 80,
              bgcolor: scannerState.isActive ? 'error.light' : 'transparent',
              '&:hover': {
                bgcolor: scannerState.isActive ? 'error.main' : 'primary.light',
              },
            }}
          >
            {isInitializingCamera ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <QrCodeScannerSharpIcon 
                fontSize="large" 
                sx={{ 
                  fontSize: 40,
                  color: scannerState.isActive ? 'white' : 'primary.main'
                }} 
              />
            )}
          </IconButton>
        </Stack>
        
        <Stack direction="row" alignItems="center" sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {scannerState.isActive ? "Tap to stop scanning" : "Scan with Camera"}
          </Typography>
          <InfoIcon text="Point your camera at a QR code to scan the student ID" />
        </Stack>
      </Box>

      {scannerState.error && (
        <Typography color="error" align="center" variant="body2">
          {scannerState.error}
        </Typography>
      )}

      {scannerState.showFileUpload && (
        <Card sx={{ mt: 2, bgcolor: 'grey.50' }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" align="center" mb={2}>
              Alternatively, upload a QR code image from your device.
            </Typography>
            <Stack direction="row" justifyContent="center" width="100%">
              <Button
                variant="outlined"
                size="medium"
                onClick={() => fileInputRef.current?.click()}
                sx={{ minWidth: '180px' }}
              >
                Upload QR Code Image
              </Button>
            </Stack>
            <input
              type="file"
              accept="image/*"
              hidden
              ref={fileInputRef}
              onChange={(e) => handleFileUpload(e, onStudentIdScanned)}
            />
          </CardContent>
        </Card>
      )}

      <div id="qr-reader-file" style={{ display: 'none' }} />
    </Stack>
  );
});

export const StudentServiceTemplateSignUpPage = () => {
  const { params } = useParams(routeName, Params);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [request, setRequest] = useState<StudentServiceTemplateSignUpRequest>(
    {} as StudentServiceTemplateSignUpRequest
  );
  const [resource, setResource] = useState<StudentServiceTemplateSignUpResource>({});
  const [studentSignature, setStudentSignature] = useState<any>({});
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const [currentTab, setCurrentTab] = useState<SignUpTab>("Student ID");
  const [student, setStudent] = useState<Student | null>();
  const [confirming, setConfirming] = useState<boolean>();
  const [isSignedIn, setIsSignedIn] = useState<boolean>();
  const [canCreatePartial, setCanCreatePartial] = useState<boolean>(false);
  const [shouldAutoAdvance, setShouldAutoAdvance] = useState(false);


  const { breakpointsRef, smallFieldBreakpoints, largeFieldBreakpoints } = useElementBreakpoints();
  const properties = resource.properties || [];

    const playSuccessSound = useCallback(() => {
        try {
            const audio = new Audio('/sounds/QR_Success_Chime.m4a');
            audio.volume = 0.8;
            audio.play().catch(error => {
                console.warn('Could not play QR success sound:', error);
            });
        } catch (error) {
            console.warn('Audio not supported:', error);
        }
    }, []);

    const playFailureSound = useCallback(() => {
        try {
            const audio = new Audio('/sounds/QR_Fail.wav');
            audio.volume = 0.8;
            audio.play().catch(error => {
                console.warn('Could not play QR failure sound:', error);
            });
        } catch (error) {
            console.warn('Audio not supported:', error);
        }
    }, []);

  const handleStudentIdScanned = useCallback((studentId: string) => {
    setRequest(
      produce((draft) => {
        draft.studentId = studentId;
      })
    );
      setShouldAutoAdvance(true);
  }, [setRequest]);

  useEffect(() => {
    getRequest();
    fetchResource();
  }, []);

  useEffect(() => {
    if (currentTab == "Student Lookup") {
      setRequest(
        produce((draft) => {
          draft.studentId = undefined;
        })
      );
    }
    if (currentTab == "Student ID") {
      setRequest(
        produce((draft) => {
          draft.studentFirstName = undefined;
          draft.studentLastName = undefined;
          draft.studentDOB = undefined;
        })
      );
    }
  }, [currentTab]);

  const getRequest = () => {
    ApiService.studentServiceTemplates
      .getSignUpRequest(params.tenantName, params.id, params.validationToken)
      .then((response) => {
        setRequest(response);
      });
  };

  const fetchResource = () => {
    ApiService.studentServiceTemplates.getSignUpResource(params.tenantName).then((response) => {
      setResource(response);
    });
  };

const signUpConfirmation = useCallback(() => {
  var temp = {
    ...request,
    requireSignature: false,
  };

  ApiService.studentServiceTemplates
    .signUpConfirmation(temp)
    .then((response) => {
      playSuccessSound();
      let sig = request.requireSignature;
      var r = {
        ...temp,
        requireSignature: sig,
        isSignedIn: response.isSignedIn,
        additionalServiceSpecificIndex: response.additionalServiceSpecificIndex,
      };
      setRequest(r);
      setStudent(response.student);
      setIsSignedIn(response.isSignedIn);
      setConfirming(true);
      setCanCreatePartial(false);
    })
    .catch((ex) => {
      if (ex.response.studentDOB && ex.response.studentLastName) {
        let sig = request.requireSignature;
        var r = {
          ...temp,
          requireSignature: sig,
        };
        setRequest(r);
        setConfirming(true);
        setCanCreatePartial(true);
      } else {
        playFailureSound();
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.response);
      }
    });
}, [request, dispatch, setValidationErrors, playSuccessSound, playFailureSound]);

  useEffect(() => {
  if (shouldAutoAdvance && request.studentId) {
    setShouldAutoAdvance(false);
    signUpConfirmation();
  }
}, [shouldAutoAdvance, request.studentId, signUpConfirmation]);

  const signUp = () => {
    const tempRequest = {
      ...request,
      isPartialStudent: canCreatePartial,
      signatureData:
        typeof studentSignature.isEmpty !== "function" || studentSignature.isEmpty()
          ? null
          : studentSignature.getTrimmedCanvas().toDataURL("image/png"),
    };

    if (request.isSignedIn) {
      tempRequest.requireSignature = false;
    }

    if (tempRequest.studentId == null && student != null) {
      tempRequest.studentId = student.customId1 || student.customId2;
    }

    ApiService.studentServiceTemplates
      .signUp(tempRequest)
      .then((response) => {
        navigate(`../${thankYouPageRoute.getPath()}`);
        dispatch(setAppToast(newToast("success")));

        setTimeout(() => {
          navigate(-1);
        }, 3000);
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.response);
      });
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    var temp = deepCopy(request.additionalServiceSpecificIndex) || [];
    if (event.target.checked) {
      temp.push(parseInt(event.target.value));
    } else {
      var removeIndex = temp.findIndex((t) => t == parseInt(event.target.value));
      temp.splice(removeIndex, 1);
    }
    setRequest(
      produce((draft) => {
        draft.additionalServiceSpecificIndex = temp;
      })
    );
  };

  const goBack = () => {
    setStudent(null);
    setConfirming(false);
    setCanCreatePartial(false);
      setRequest(
          produce((draft) => {
              draft.studentId = undefined;
              draft.studentFirstName = undefined;
              draft.studentLastName = undefined;
              draft.studentDOB = undefined;
          })
      );
  };

  let pageContent;

  if (request.isActive) {
    if (student && confirming) {
      pageContent = (
        <Stack spacing="lg" alignItems={"center"}>
          <Box>
            <Logo width="100px" animate />
          </Box>
          <Box
            sx={{
              width: { xs: "100%", sm: "80%", md: "60%" },
              boxShadow: 3,
              borderRadius: 2,
              p: 4,
              mt: 4,
              textAlign: "center",
            }}
          >
            <Typography variant="h6" mb={2}>
              Please confirm these details are correct
            </Typography>
            <Grid container spacing={GRID_SPACING as any} justifyContent="center">
              <Grid item xs={12} sm={6}>
                <DataDisplay label="Name">{student?.fullName}</DataDisplay>
              </Grid>
              <Grid item xs={12} sm={6}>
                <DataDisplay label="Date of Birth">{formatDate(student?.dateOfBirth)}</DataDisplay>
              </Grid>
              {request.requireSignature && !request.isSignedIn && (
                <Grid item xs={12} mt={2}>
                  <Stack alignItems="center">
                    <SignUpSignature
                      label="Participant Signature"
                      validationErrors={validationErrors}
                      signature={studentSignature}
                      setSignature={setStudentSignature}
                    />
                  </Stack>
                </Grid>
              )}
              <Grid item xs={12} mt={2}>
                <Stack spacing="lg" direction="row" justifyContent="center">
                  <Button onClick={goBack} variant="contained" size="large">
                    Back
                  </Button>
                  <Button
                    onClick={signUp}
                    variant="contained"
                    size="large"
                    disabled={
                      !request.studentId && (!request.studentDOB || !request.studentFirstName || !request.studentLastName)
                    }
                  >
                    {isSignedIn ? "Check Out" : "Confirm"}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Stack>
      );
    } else if (canCreatePartial && confirming) {
      pageContent = (
        <Stack spacing="lg" alignItems={"center"}>
          <Box>
            <Logo width="100px" animate />
          </Box>
          <Box
            sx={{
              width: { xs: "100%", sm: "80%", md: "60%" },
              boxShadow: 3,
              borderRadius: 2,
              p: 4,
              mt: 4,
              textAlign: "center",
            }}
          >
            <Typography variant="h6" mb={2}>Student not found, would you like to continue anyway?</Typography>
            <Grid container spacing={GRID_SPACING as any}>
              <Grid item md={3} xs={12}>
                <DataDisplay label="Name">
                  {request?.studentFirstName} {request?.studentLastName}
                </DataDisplay>
              </Grid>
              <Grid item md={3} xs={12}>
                <DataDisplay label="Date of Birth">{formatDate(request?.studentDOB)}</DataDisplay>
              </Grid>
              <GridBreak />
              {request.isForParent && (
                <Grid item xs={12}>
                  <Input
                    label="Number of Parents Per Student"
                    required
                    value={request.parentCount}
                    onChange={(e) =>
                      setRequest(
                        produce((draft) => {
                          draft.parentCount = parseFloat(e.target.value);
                        })
                      )
                    }
                  />
                  {validationErrors?.[pn<StudentServiceTemplateSignUpRequest>("parentCount")] && (
                    <Typography color="error">
                      {validationErrors?.[pn<StudentServiceTemplateSignUpRequest>("parentCount")]?.[0]}
                    </Typography>
                  )}
                </Grid>
              )}
              {request.requireSignature && !request.isSignedIn && (
                <Grid item xs={12} mt={2}>
                  <Stack alignItems="center">
                    <SignUpSignature
                      label="Participant Signature"
                      validationErrors={validationErrors}
                      signature={studentSignature}
                      setSignature={setStudentSignature}
                    />
                  </Stack>
                </Grid>
              )}
              {!request.isSignedIn && request.additionalServices && request.additionalServices.length > 0 && (
                <Grid item xs={largeFieldBreakpoints}>
                  <FormControl>
                    {<FormLabel id="additional-services">Select Services:</FormLabel>}
                    <MuiFormGroup>
                      {request.additionalServices.map((o, index) => (
                        <Stack key={index} direction="row">
                          <FormControlLabel
                            key={index}
                            value={index}
                            control={
                              <Checkbox
                                checked={request.additionalServiceSpecificIndex?.includes(index)}
                                onChange={handleChange}
                                name={o}
                              />
                            }
                            label={o}
                          />
                        </Stack>
                      ))}
                    </MuiFormGroup>
                  </FormControl>
                  {validationErrors?.[`additionalServices`] && (
                    <Grid item xs={12}>
                      <Typography color="error">{validationErrors["additionalServices"].toString()}</Typography>
                    </Grid>
                  )}
                </Grid>
              )}
            </Grid>
            <Stack sx={{ spacing: 2 }} mt={2} justifyContent="center" direction="row">
              <Button onClick={goBack} variant="contained" size="large">
                Back
              </Button>
              <Button
                onClick={signUp}
                variant="contained"
                size="large"
                disabled={
                  !request.studentId && (!request.studentDOB || !request.studentFirstName || !request.studentLastName)
                }
              >
                {isSignedIn ? "Check Out" : "Confirm"}
              </Button>
            </Stack>
          </Box>
        </Stack>
      );
    } else {
      pageContent = (
        <Stack spacing="lg" alignItems={"center"}>
          <Box>
            <Logo width="100px" animate />
          </Box>
          <Box
            sx={{
              width: { xs: "100%", sm: "80%", md: "60%" },
              boxShadow: 3,
              borderRadius: 2,
              p: 4,
              mt: 4,
            }}
          >
            <Grid container spacing={GRID_SPACING as any}>
              <Grid item xs={6} md={3}>
                <DataDisplay
                  label={
                    properties.find((x) => x.propertyName === pn<StudentServiceTemplateSignUpRequest>("date"))
                      ?.displayName
                  }
                >
                  {formatDate(request?.date)}
                </DataDisplay>
              </Grid>
              <Grid item xs={6} md={3}>
                <DataDisplay
                  label={
                    properties.find((x) => x.propertyName === pn<StudentServiceTemplateSignUpRequest>("serviceName"))
                      ?.displayName
                  }
                >
                  {request?.serviceName}
                </DataDisplay>
              </Grid>
              <Grid item xs={6} md={3}>
                <DataDisplay
                  label={
                    properties.find((x) => x.propertyName === pn<StudentServiceTemplateSignUpRequest>("serviceType"))
                      ?.displayName
                  }
                >
                  {request?.serviceType}
                  {request.isForParent && <EscalatorWarningIcon style={{ marginLeft: "5px" }} />}
                </DataDisplay>
              </Grid>
              {request?.durationInMinutes && (
                <Grid item xs={6} md={3}>
                  <DataDisplay
                    label={
                      properties.find(
                        (x) => x.propertyName === pn<StudentServiceTemplateSignUpRequest>("durationInMinutes")
                      )?.displayName
                    }
                  >
                    {request?.durationInMinutes} {request?.durationInMinutes != null ? "minutes" : "N/A"}
                  </DataDisplay>
                </Grid>
              )}
              <Grid item xs={12}>
                <DataDisplay
                  label={
                    properties.find((x) => x.propertyName === pn<StudentServiceTemplateSignUpRequest>("providers"))
                      ?.displayName
                  }
                >
                  <DataArray value={request?.providers} />
                </DataDisplay>
              </Grid>
            </Grid>
            <TabContext value={currentTab}>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <TabList
                  onChange={(e, value) => setCurrentTab(value)}
                  variant="standard"
                  scrollButtons
                  allowScrollButtonsMobile
                >
                  {tabs.map((tab) => (
                    <Tab
                      key={tab}
                      label={
                        tab == "Student ID" && resource.customIdTypes && resource.customIdTypes.length > 0
                          ? resource.customIdTypes[0].name
                          : tab
                      }
                      value={tab}
                    />
                  ))}
                </TabList>
              </Box>
              {currentTab === "Student ID" && (
                <Stack spacing={STACK_SPACING as any} sx={{ mt: 2 }}>
                  <Input
                    label={
                      resource.customIdTypes && resource.customIdTypes.length > 0
                        ? resource.customIdTypes[0].name
                        : "Student ID"
                    }
                    errors={validationErrors?.[pn<StudentServiceTemplateSignUpRequest>("studentId")]}
                    value={request?.studentId || ""}
                     onChange={(e) =>
                            setRequest(
                              produce((draft) => {
                                draft.studentId = e.target.value;
                              })
                            )
                          }
                        />
                  
                  <QRScannerErrorBoundary onError={(error) => console.error('QR Scanner failed:', error)}>
                    <QRScannerSection
                      onStudentIdScanned={handleStudentIdScanned}
                      isActive={request.isActive}
                      currentStudentId={request?.studentId || ""}
                    />
                  </QRScannerErrorBoundary>
                </Stack>
              )}
              {currentTab == "Student Lookup" && (
                <Grid ref={breakpointsRef} container spacing={GRID_SPACING as any} sx={{ mt: 2 }}>
                  <Grid item md={6} xs={12}>
                    <Input
                      label="First Name"
                      errors={validationErrors?.[pn<StudentServiceTemplateSignUpRequest>("studentFirstName")]}
                      value={request?.studentFirstName}
                      onChange={(e) =>
                        setRequest(
                          produce((draft) => {
                            draft.studentFirstName = e.target.value;
                          })
                        )
                      }
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <Input
                      label="Last Name"
                      value={request?.studentLastName}
                      errors={validationErrors?.[pn<StudentServiceTemplateSignUpRequest>("studentLastName")]}
                      onChange={(e) =>
                        setRequest(
                          produce((draft) => {
                            draft.studentLastName = e.target.value;
                          })
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <DatePicker
                      label="Date of Birth"
                      modelProperty={properties.find(
                        (x) => x.propertyName === pn<StudentServiceTemplateSignUpRequest>("studentDOB")
                      )}
                      errors={validationErrors?.[pn<StudentServiceTemplateSignUpRequest>("studentDOB")]}
                      value={request?.studentDOB}
                      onChange={(value) =>
                        setRequest(
                          produce((draft) => {
                            draft.studentDOB = value;
                          })
                        )
                      }
                    />
                  </Grid>
                </Grid>
              )}
            </TabContext>
            <Stack direction="row" justifyContent="center" width="100%" sx={{ mt: 3 }}>
              <Button
                onClick={signUpConfirmation}
                variant="contained"
                size="medium"
                disabled={
                  !request.studentId && (!request.studentDOB || !request.studentFirstName || !request.studentLastName)
                }
                sx={{ width: 'auto', minWidth: '120px' }}
              >
                Next
              </Button>
            </Stack>
          </Box>
        </Stack>
      );
    }
  } else {
    pageContent = (
      <Stack spacing="lg" alignItems={"center"}>
        <Box>
          <Logo width="100px" animate />
        </Box>
        <Typography>This link is no longer active.</Typography>
      </Stack>
    );
  }

  return <Page>{pageContent}</Page>;
};